import { useNavigate, useSearchParams, Navigate, Link } from 'react-router-dom'
import { gql } from 'graphql-request'
import { useState, useEffect } from 'react'
import { UserPlusIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline'

import { useCurrentUser } from '@contexts/currentUser'
import { useQuery, useMutation } from '@hooks/graphql'
import { Button } from '@components/ui/button'
import { useFlashMessage } from '@components/FlashMessage'

import Spinner from '@components/Spinner'
import Notification from '@components/Notification'
import SplitLayout from '@components/Onboarding/Layout/SplitLayout'
import SignUpForm from './SignUpForm'
import SelectList from '@components/ui/selectlist'

const ORGANIZATION_BY_INVITATION_TOKEN_QUERY = gql`
  query organizationByInvitationToken($invitationToken: String!) {
    organizationByInvitationToken(invitationToken: $invitationToken) {
      id
      name
    }
  }
`

const JOIN_ORGANIZATION_MUTATION = gql`
  mutation joinOrganization($input: JoinOrganizationInput!) {
    joinOrganization(input: $input) {
      success
      errors {
        message
      }
    }
  }
`

const EducatorAcceptInvitation = () => {
  const { setFlashMessage } = useFlashMessage()
  const [searchParams] = useSearchParams()
  const invitationToken = searchParams.get('invitation_token')
  const navigate = useNavigate()
  const { user: { signedIn, single_organization_membership: singleOrganizationMembership } = {}, currentMembership: { role } = {}, refetchUser, switchOrganization } = useCurrentUser()
  const [showSignUpForm, setShowSignUpForm] = useState(false)
  const [decision, setDecision] = useState(null)

  const signInUrl = `/sign_in?redirect_to=${window.location.pathname + window.location.search}`

  useEffect(() => {
    if (decision === 'new') setShowSignUpForm(true)
    if (decision === 'existing') navigate(signInUrl)
  }, [decision])

  const { data: { organizationByInvitationToken: organization } = {}, isLoading } = useQuery({
    queryKey: ['organizationByInvitationToken', invitationToken],
    gqlQuery: ORGANIZATION_BY_INVITATION_TOKEN_QUERY,
    variables: { invitationToken },
    disabled: !invitationToken
  })

  const { mutateAsync: joinOrganization, isLoading: isJoining, isError } = useMutation({
    gqlMutation: JOIN_ORGANIZATION_MUTATION,
    onSuccess: response => {
      if (response.joinOrganization.success) {
        refetchUser()
        switchOrganization(organization.id)
        setFlashMessage(`You have joined ${organization.name} 🎉`, 'success')
        navigate('/educators')
      }
    }
  })

  if (isLoading) return <Spinner className='relative top-0 left-0 flex items-center justify-center w-full h-screen' />

  return (
    <SplitLayout>
      <Choose>
        <When condition={!organization}>
          <Notification theme='error' className='my-3'>
            Hmmm we couldn't find that organization 🤔
          </Notification>

          <p>Oops! Your invitation doesn't seem to be working.</p>
          <p>You may have used it already, or it expired.</p>
        </When>

        <When condition={signedIn}>
          <h1 className='text-2xl font-bold tracking-tight mt-3'>Join {organization.name}</h1>

          <h2 className='text-base mt-3'>
            You are joining <span className='font-semibold'>{organization.name}</span>.
          </h2>

          <If condition={isError}>
            <Notification theme='error' className='mt-3'>
              Oops, something went wrong 🤔
            </Notification>
          </If>

          <Button
            loading={isJoining}
            className='mt-5 w-full'
            onClick={() => joinOrganization({ input: { invitationToken } })}
          >
            Join organization
          </Button>
        </When>

        <Otherwise>
          <h1 className='text-2xl font-bold tracking-tight mt-3'>Welcome to Mindjoy!</h1>
          <h2 className='text-base mt-2'>
            You are joining <span className='font-semibold'>{organization.name}</span>.
          </h2>

          <Choose>
            <When condition={showSignUpForm}>
              <SignUpForm />
            </When>
            <Otherwise>
              <SelectList
                value={decision}
                onChange={setDecision}
                options={[
                  {
                    emoji: '🐣',
                    label: 'I\'m new to Mindjoy',
                    value: 'new'
                  },
                  {
                    emoji: '👔',
                    label: 'I already have an account',
                    value: 'existing'
                  }
                ]}
              />
            </Otherwise>
          </Choose>
        </Otherwise>
      </Choose>
      <If condition={showSignUpForm}>
        <p className='text-center mt-5 mb-5 text-sm'>
          Already have an account? <Link className='underline' to={signInUrl}>Sign in</Link>
        </p>
      </If>

      <SplitLayout.SidePanel>
        <div className="h-full w-full bg-right bg-no-repeat bg-contain bg-[url(/onboarding/classroom-insights@2x.png)]" />
      </SplitLayout.SidePanel>
    </SplitLayout>
  )
}

export default EducatorAcceptInvitation
